import React from "react";
import { Table } from "antd";
import { BorderedButton } from "../../../commonComponents";
import { useScreenWidth } from "../../../../utils/useScreenWidth";
import { getLeagueImage } from "../../../../helpers/leagueLogo";
import { formatUSD } from "../../../../helpers/formatCurrency";
import WonTick from "../../../../assets/images/won-tick.png";
import LostTick from "../../../../assets/images/lost-tick.png";
import "./PastTable.scss";

// Backend fields (outcome / prize_amount / entry_cost) are additive and not
// deployed yet as of this change - see qa/investigations. Render gracefully
// when they're absent rather than guessing: hides the whole outcome line
// instead of printing "Won $undefined" / "Won $NaN". Never derive this from
// the deprecated `record.message` string.
const getOutcomeText = (record) => {
  if (record?.outcome === "Won") {
    const amount = formatUSD(record?.prize_amount);
    return amount ? `Won ${amount}` : null;
  }
  if (record?.outcome === "Lost") {
    const amount = formatUSD(record?.entry_cost);
    return amount ? `Lost ${amount}` : null;
  }
  return null;
};

// Per-entry ✅/❌ next to a settled selection, same WonTick/LostTick icons
// OngoingTable uses for its entry_1/2/3 rows. Assumes each team in the
// `teams` array carries its own `status` ("Won"/"Lost") the same way
// OngoingTable's entries do - unconfirmed against the new multi-entry
// payload shape, so this renders nothing for any other value instead of
// guessing.
const EntryResultIcon = ({ status }) => {
  if (status === "Lost") {
    return <img src={LostTick} alt="Lost" className="result-icon" />;
  }
  if (status === "Won") {
    return <img src={WonTick} alt="Won" className="result-icon" />;
  }
  return null;
};

const PastTable = ({ pastTableData }) => {
  const screenWidth = useScreenWidth();

  const columns = [
    {
      title: "League",
      dataIndex: "competition",
      render: (_, record) => {
        return (
          <div className="ongoing-table-competitions">
            <img
              src={getLeagueImage(record.competition.name, record.competition.image_path)}
              alt="League Icon"
              className="ongoing-table-competitions-image"
              style={{
                width: record?.sport_id == 2 ? "14px" : "36px",
                height: record?.sport_id == 2 ? "25px" : "36px",
              }}
            />
            <div>{record.competition.name}</div>
          </div>
        );
      },
    },
    {
      title: "Game Title",
      dataIndex: "game_title",
    },
    {
      title: "Pot",
      dataIndex: "pot",
      render: (_, record) => {
        return <div>{formatUSD(record.pot)}</div>;
      },
    },
    {
      title: "Round",
      dataIndex: "round",
    },
    {
      title: "My Selections",
      dataIndex: "teams",
      render: (_, record) => {
        return record?.teams?.map((item, index) => {
          return (
            <div key={index + 1}>
              <div className="past-table-selection">
                <img
                  src={item.image}
                  alt="Team Icon"
                  className="ongoing-table-selections-image"
                />
                <div>{item.name}</div>
              </div>
              <div className="ongoing-table-selections-opponent">
                vs {item?.opponent?.name} &nbsp; &nbsp;
                {/* Was className="white-color", which also carries Layout.scss's
                    page-shell "background-color: #000" rule (same global class,
                    unrelated intent) - boxed the score in solid black. Dedicated
                    class below keeps the white text without inheriting that
                    background. */}
                <span className="past-table-score">
                  {item.opponent?.my_score} -&nbsp;
                  {item.opponent?.score}
                </span>
                <EntryResultIcon status={item?.status} />
              </div>
            </div>
          );
        });
      },
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      render: (_, record) => {
        const outcomeText = getOutcomeText(record);
        return (
          <div className="past-table-conts">
            {outcomeText && (
              <div className="past-table-outcome-text font-cairo-bold primary-color">
                {outcomeText}
              </div>
            )}
            <div>
              <BorderedButton
                name={"View All Selections"}
                link={"/dashboard/allselections"}
                btnHeight={"45px"}
                btnClass={"past-table-actions-button"}
                dataToSend={{
                  id: record.id,
                  title: record.game_title,
                  round: record.round,
                }}
              />
            </div>
          </div>
        );
      },
    },
  ];
  return (
    <div className="ongoing-table position-relative">
      {screenWidth >= 767 ? (
        <Table
          columns={columns}
          dataSource={pastTableData}
          className="past-table-cont font-cairo-normal"
          rowClassName="past-table-row"
          pagination={false}
        />
      ) : (
        <div className="past-mobile">
          {pastTableData?.map((item, index) => {
            return (
              <div
                className="past-mobile-cont font-cairo-normal"
                key={index + 1}
              >
                <div className="past-mobile-cont-items">
                  {item?.is_turbo && (
                    <div className="past-mobile-turbotag font-cairo-bold">
                      Turbo
                    </div>
                  )}
                  <div className="past-mobile-cont-items-title">League</div>
                  <div className="ongoing-table-competitions">
                    <img
                      src={getLeagueImage(
                        item.competition.name,
                        item.competition.image_path
                      )}
                      alt="League Icon"
                      className="ongoing-table-competitions-image"
                    />
                    <div>{item.competition.name}</div>
                  </div>
                </div>
                <div className="past-mobile-cont-items">
                  <div className="past-mobile-cont-items-title">Game Title</div>
                  <div>{item.game_title}</div>
                </div>
                <div className="past-mobile-cont-items">
                  <div className="past-mobile-cont-items-title">Round</div>
                  <div>{item.round}</div>
                </div>
                <div className="past-mobile-cont-items">
                  <div className="past-mobile-cont-items-title">
                    My {item?.teams?.length > 1 ? "Selections" : "Selection"}
                  </div>
                  <div>
                    {item.teams.map((team, index) => {
                      return (
                        <>
                          <div className="past-mobile-teams" key={index + 1}>
                            <img
                              src={team.image}
                              alt="Team Icon"
                              className="past-mobile-teamicon"
                            />
                            <span>{team.name}</span>
                            <EntryResultIcon status={team?.status} />
                          </div>
                          <div className="ongoing-table-selections-opponent">
                            vs {team?.opponent?.name} &nbsp; &nbsp;
                            <span className="past-table-score">
                              {team.opponent?.my_score} -&nbsp;
                              {team.opponent?.score}
                            </span>
                          </div>
                        </>
                      );
                    })}
                  </div>
                </div>
                {getOutcomeText(item) && (
                  <div className="past-mobile-cont-items">
                    <div className="past-mobile-cont-items-title">Outcome</div>
                    <div className="past-table-outcome-text font-cairo-bold primary-color">
                      {getOutcomeText(item)}
                    </div>
                  </div>
                )}
                <div className="past-mobile-cont-items justify-center">
                  <BorderedButton
                    name={"View All Selections"}
                    link={"/dashboard/allselections"}
                    btnHeight={"54px"}
                    btnClass={"past-mobile-button"}
                    dataToSend={{
                      id: item.id,
                      title: item.game_title,
                      round: item.round,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
export default PastTable;
